import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Center,
  Container,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Icon,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  Tooltip,
  useDisclosure,
  useMediaQuery,
  useToast,
  VStack,
} from '@chakra-ui/react';
import {
  AddOutlined,
  CloseOutlined,
  CloudUploadOutlined,
  InsertDriveFileOutlined,
} from '@mui/icons-material';
import {
  FC,
  MutableRefObject,
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  Accept,
  DropEvent,
  DropzoneState,
  FileRejection,
  useDropzone,
} from 'react-dropzone';
import { FieldErrorsImpl, useFormContext } from 'react-hook-form';

import { showToast } from '../toast/toast';

interface ChakraDropzoneMultipleProps
  extends Omit<
    React.DetailedHTMLProps<
      React.InputHTMLAttributes<HTMLInputElement>,
      HTMLInputElement
    >,
    'accept'
  > {
  name: string;
  header?: string;
  disabled?: boolean;
  required?: boolean;
  maxFiles?: number;
  maxSize?: number;
  errors?: Partial<FieldErrorsImpl<{ [x: string]: string }>>;
  accept?: Accept;
  isCentered?: boolean;
  onRemoveFile: (idx: number) => void;
  onDropzoneInit?: (dropzoneState: DropzoneState) => void;
}

export const DropzoneMultiple: FC<ChakraDropzoneMultipleProps> = ({
  name,
  header,
  disabled,
  errors,
  accept,
  maxSize = 0,
  maxFiles,
  isCentered = false,
  onRemoveFile,
  onDropzoneInit,
}) => {
  const toast = useToast();
  const [isMobile] = useMediaQuery('(max-width: 768px)');
  const [filesSave, setFilesSave] = useState<File[]>([]);
  const { register, unregister, setValue } = useFormContext();
  const errorForm = errors ? errors[name]?.message : '';
  const [errorMessage, setErrorMessage] = useState<string>(errorForm as string);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [filePrev, setFilePrev] = useState<string>('');
  const [fileType, setFileType] = useState<string>('');
  const closeDrawerCallbackRef = useRef<(() => void) | null>(null);

  const isMaxFilesReached = maxFiles ? filesSave.length >= maxFiles : false;

  const onDrop = useCallback(
    (droppedFiles: File[]) => {
      if (droppedFiles.length > 0) {
        // Calculate available slots
        const availableSlots = maxFiles
          ? maxFiles - filesSave.length
          : Infinity;

        // Already at max limit
        if (availableSlots <= 0) {
          const idToast = 'max-files-reached';
          if (!toast.isActive(idToast)) {
            showToast(
              toast,
              'error',
              `Maksimal ${maxFiles} file yang dapat diunggah`,
              idToast
            );
          }
          return;
        }

        let filesToProcess: File[];
        // Check if dropped files exceed available slots
        if (droppedFiles.length > availableSlots) {
          filesToProcess = droppedFiles.slice(0, availableSlots);
          const rejectedCount = droppedFiles.length - availableSlots;

          const idToast = 'files-limit-exceeded';
          if (!toast.isActive(idToast)) {
            showToast(
              toast,
              'error',
              `Maksimal ${maxFiles} file. Hanya ${availableSlots} file pertama yang diterima, ${rejectedCount} file lainnya diabaikan.`,
              idToast
            );
          }
        } else {
          filesToProcess = droppedFiles;
        }

        // Filter out duplicate files
        const newFiles: File[] = [];
        const duplicateFiles: string[] = [];
        filesToProcess.forEach((file) => {
          const exists = filesSave.some(
            (item) => item.name === file.name && item.size === file.size
          );
          if (!exists) {
            newFiles.push(file);
          } else {
            duplicateFiles.push(file.name);
          }
        });

        // Show toast for duplicate files
        if (duplicateFiles.length > 0) {
          const idToast = 'file-bukti-bayar';
          if (!toast.isActive(idToast)) {
            showToast(
              toast,
              'error',
              `File berikut sudah ada: ${duplicateFiles.join(', ')}`,
              idToast
            );
          }
        }

        // Accumulate new files with existing ones and sync to form
        if (newFiles.length > 0) {
          const updatedFiles = [...filesSave, ...newFiles];
          setFilesSave(updatedFiles);
          setValue(name, updatedFiles, { shouldValidate: true });
        }

        // Close drawer immediately after file is selected (Android fix)
        if (closeDrawerCallbackRef.current) {
          closeDrawerCallbackRef.current();
          closeDrawerCallbackRef.current = null;
        }
      }
    },
    [setValue, name, maxFiles, filesSave, toast]
  );

  function fileValidator(file: File) {
    const allowedTypes = [
      '.jpg',
      '.jpeg',
      '.png',
      '.pdf',
      'image/jpeg',
      'image/png',
      'application/pdf',
    ];

    if (!allowedTypes.includes(file.type)) {
      return {
        code: 'invalid-file',
        message: `Hanya menerima JPG/PNG/PDF`,
      };
    }

    if (file.size > maxSize * 1000000) {
      return {
        code: 'file-too-large',
        message: `File is larger than ${maxSize} MB`,
      };
    }

    return null;
  }

  function onDropRejected(fileRejections: FileRejection[], event: DropEvent) {
    setErrorMessage(fileRejections[0].errors[0].message ?? 'Invalid file type');
  }

  function onDropAccepted() {
    setErrorMessage('');
  }

  const fileDropzone = useDropzone({
    onDrop,
    onDropRejected,
    onDropAccepted,
    validator: fileValidator,
    accept: accept,
    disabled: disabled,
    noClick: true,
    noKeyboard: true,
    multiple: true, // Allow multiple file selection
  });

  const { getRootProps } = fileDropzone;
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    if (onDropzoneInit && !hasInitializedRef.current) {
      hasInitializedRef.current = true;
      onDropzoneInit(fileDropzone);
    }
  }, [onDropzoneInit, fileDropzone]);

  useEffect(() => {
    register(name);
    return () => {
      unregister(name);
    };
  }, [register, unregister, name]);

  const handlePreview = (file: File) => {
    const tmpFilePath = URL.createObjectURL(file);
    setFilePrev(tmpFilePath);
    setFileType(file.type);
    setIsViewerOpen(true);
  };

  const removeFile = (index: number) => {
    const newItems = [...filesSave];
    newItems.splice(index, 1);
    setFilesSave(newItems);
    // Sync form value with remaining files for form submission
    setValue(name, newItems);
    onRemoveFile(index);
  };

  const acceptedFileItems = filesSave?.map((file: File, index: number) => {
    const fileName =
      file?.name.length > 18 ? `${file?.name.slice(0, 18)}...` : file?.name;

    return (
      <Stack key={index} textAlign="center">
        <Stack direction="row" justifyContent="center">
          <Card
            width="fit-content"
            boxShadow="none"
            size={{ base: 'sm', md: 'md' }}
          >
            <CardHeader padding={0} textAlign="end">
              <IconButton
                variant="ghost"
                aria-label="delete-file"
                border="1px"
                borderRadius="full"
                color="gray.75"
                size="xs"
                icon={<CloseOutlined fontSize="small" />}
                onClick={() => removeFile(index)}
              />
            </CardHeader>
            <CardBody paddingTop={0} _hover={{ cursor: 'pointer' }}>
              <Icon
                as={InsertDriveFileOutlined}
                height={{ base: 10, md: 20 }}
                width={{ base: 10, md: 20 }}
                marginRight={1}
                padding={2}
                bgColor="gray.33"
                color="blue"
                borderRadius="md"
                onClick={() => handlePreview(file)}
              />
            </CardBody>
          </Card>
        </Stack>
        <Tooltip label={file?.name} aria-label={file?.name}>
          <Text fontSize={{ base: '12px', md: '16px' }}>{fileName}</Text>
        </Tooltip>
      </Stack>
    );
  });

  return (
    <Stack>
      <Container
        as={Center}
        {...getRootProps()}
        maxW="full"
        bgColor={`${filesSave.length > 0 ? 'white' : 'gray.customGray'}`}
        border="dashed"
        borderRadius="md"
        borderColor="gray.75"
        centerContent
        overflowX="auto"
        h={`${isMobile ? 56 : 60}`}
      >
        {filesSave.length > 0 ? (
          <Stack alignSelf="flex-start" direction="row" spacing={4}>
            {acceptedFileItems}
            {!isMaxFilesReached && (
              <Stack textAlign="center">
                {isMobile ? (
                  <AddMoreFileMobile
                    dropzoneFileState={fileDropzone}
                    closeDrawerCallbackRef={closeDrawerCallbackRef}
                  />
                ) : (
                  <AddMoreFileDesktop dropzoneState={fileDropzone} />
                )}
                <Text
                  textColor="blue"
                  fontWeight="semibold"
                  fontSize={{ base: '12px', md: '16px' }}
                >
                  Tambah File
                </Text>
              </Stack>
            )}
          </Stack>
        ) : isMobile ? (
          <DropzoneUploadMobile
            dropzoneFileState={fileDropzone}
            closeDrawerCallbackRef={closeDrawerCallbackRef}
          />
        ) : (
          <DropzoneUploadDesktop dropzoneState={fileDropzone} />
        )}
      </Container>
      <Text textColor="red">{errorMessage}</Text>
      <Modal
        isOpen={isViewerOpen}
        onClose={() => {
          setIsViewerOpen(false);
          setFilePrev('');
          setFileType('');
        }}
        closeOnEsc
        size="3xl"
        isCentered={isCentered}
      >
        <ModalOverlay />
        <ModalContent>
          {header && <ModalHeader>{header}</ModalHeader>}
          <ModalBody margin="auto" overflowY="auto" p="0">
            {fileType === 'image/png' ||
            fileType === 'image/jpg' ||
            fileType === 'image/jpeg' ? (
              <img
                src={filePrev}
                alt="preview file"
                width="100%"
                height="100%"
              />
            ) : (
              <object
                data={filePrev}
                title="preview file"
                width={isMobile ? '100%' : '600px'}
                height="700px"
              />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Stack>
  );
};

type DropzoneUploadDesktopProps = {
  dropzoneState: DropzoneState;
};

function DropzoneUploadDesktop({ dropzoneState }: DropzoneUploadDesktopProps) {
  const { getInputProps, open } = dropzoneState;
  return (
    <>
      <input {...getInputProps()} />
      <Icon
        as={CloudUploadOutlined}
        fontSize={{ base: '4xl', lg: '7xl' }}
        color="blue"
      />
      <Text fontSize={{ base: 'sm', lg: 'md' }} textAlign="center">
        Tarik dan lepas file disini
      </Text>
      <Text fontSize="sm" textAlign="center" mt="2">
        atau
      </Text>
      <Button
        variant="ghost"
        textColor="blue"
        _hover={{ bgColor: 'transparent' }}
        onClick={open}
        pb={2}
      >
        Cari file
      </Button>
    </>
  );
}

type DropzoneUploadMobileProps = {
  dropzoneFileState: DropzoneState;
  closeDrawerCallbackRef?: MutableRefObject<(() => void) | null>;
};

function DropzoneUploadMobile({
  dropzoneFileState,
  closeDrawerCallbackRef,
}: DropzoneUploadMobileProps) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const btnRef = useRef(null);
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <>
      {/* Only render one input to avoid Android WebView confusion */}
      <input {...dropzoneFileState.getInputProps()} ref={inputRef} />
      <Icon
        as={CloudUploadOutlined}
        fontSize={{ base: '4xl', lg: '7xl' }}
        color="blue"
      />
      <>
        <Button
          variant="ghost"
          textColor="blue"
          pb={2}
          _hover={{ bgColor: 'transparent' }}
          ref={btnRef}
          colorScheme="teal"
          onClick={onOpen}
        >
          Unggah
        </Button>
        <DrawerMobile
          dropzoneFileState={dropzoneFileState}
          isOpen={isOpen}
          onClose={onClose}
          btnRef={btnRef}
          closeDrawerCallbackRef={closeDrawerCallbackRef}
          inputRef={inputRef}
        />
      </>
    </>
  );
}

function AddMoreFileDesktop({ dropzoneState }: DropzoneUploadDesktopProps) {
  return (
    <>
      <input {...dropzoneState.getInputProps()} />
      <Stack direction="row" justifyContent="center">
        <Card
          width="fit-content"
          boxShadow="none"
          size={{ base: 'sm', md: 'md' }}
        >
          <CardBody marginTop={{ base: 2, md: 0 }}>
            <IconButton
              aria-label="add-item"
              height={{ base: 10, md: 20 }}
              width={{ base: 10, md: 20 }}
              color="white"
              bgColor="rgba(0, 0, 163, 0.5);"
              border="3px"
              borderStyle="dashed"
              borderColor="blue"
              borderRadius="md"
              as={AddOutlined}
              onClick={dropzoneState.open}
            />
          </CardBody>
        </Card>
      </Stack>
    </>
  );
}

function AddMoreFileMobile({
  dropzoneFileState,
  closeDrawerCallbackRef,
}: DropzoneUploadMobileProps) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const btnRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <>
      {/* Only render one input to avoid Android WebView confusion */}
      <input {...dropzoneFileState.getInputProps()} ref={inputRef} />

      <Stack direction="row" justifyContent="center">
        <Card
          width="fit-content"
          boxShadow="none"
          size={{ base: 'sm', md: 'md' }}
        >
          <CardBody marginTop={{ base: 2, md: 0 }}>
            <>
              <IconButton
                aria-label="add-item"
                height={{ base: 10, md: 20 }}
                width={{ base: 10, md: 20 }}
                color="white"
                bgColor="rgba(0, 0, 163, 0.5);"
                border="3px"
                borderStyle="dashed"
                borderColor="blue"
                borderRadius="md"
                as={AddOutlined}
                onClick={onOpen}
              />
              <DrawerMobile
                dropzoneFileState={dropzoneFileState}
                isOpen={isOpen}
                onClose={onClose}
                btnRef={btnRef}
                closeDrawerCallbackRef={closeDrawerCallbackRef}
                inputRef={inputRef}
              />
            </>
          </CardBody>
        </Card>
      </Stack>
    </>
  );
}

type DrawerMobileProps = {
  dropzoneFileState: DropzoneState;
  isOpen: boolean;
  onClose: () => void;
  btnRef: RefObject<HTMLButtonElement>;
  closeDrawerCallbackRef?: MutableRefObject<(() => void) | null>;
  inputRef?: RefObject<HTMLInputElement>;
};

function DrawerMobile({
  dropzoneFileState,
  isOpen,
  onClose,
  btnRef,
  closeDrawerCallbackRef,
  inputRef,
}: DrawerMobileProps) {
  const handleGalleryClick = () => {
    try {
      // Store the close callback to be called after file is selected
      if (closeDrawerCallbackRef) {
        closeDrawerCallbackRef.current = onClose;
      }

      // Trigger file input click directly for better Android compatibility
      if (inputRef?.current) {
        inputRef.current.click();
      } else {
        dropzoneFileState.open();
      }
    } catch (error) {
      console.error('Error opening file picker:', error);
      // If error occurs, close immediately and clear callback
      if (closeDrawerCallbackRef) {
        closeDrawerCallbackRef.current = null;
      }
      onClose();
    }
  };

  const handleFileManagerClick = () => {
    try {
      // Store the close callback to be called after file is selected
      if (closeDrawerCallbackRef) {
        closeDrawerCallbackRef.current = onClose;
      }

      // Trigger file input click directly for better Android compatibility
      if (inputRef?.current) {
        inputRef.current.click();
      } else {
        dropzoneFileState.open();
      }
    } catch (error) {
      console.error('Error opening file picker:', error);
      // If error occurs, close immediately and clear callback
      if (closeDrawerCallbackRef) {
        closeDrawerCallbackRef.current = null;
      }
      onClose();
    }
  };

  return (
    <Drawer
      isOpen={isOpen}
      placement="bottom"
      onClose={onClose}
      finalFocusRef={btnRef}
    >
      <DrawerOverlay />
      <DrawerContent>
        <DrawerHeader textAlign="center">Unggah File</DrawerHeader>

        <DrawerBody pt={2} pb={5}>
          <VStack>
            <Button
              w="full"
              variant="outline"
              textColor="blue"
              fontWeight="normal"
              _hover={{ bgColor: 'transparent' }}
              onClick={handleGalleryClick}
            >
              Pilih dari galeri
            </Button>
            <Button
              w="full"
              variant="outline"
              textColor="blue"
              fontWeight="normal"
              _hover={{ bgColor: 'transparent' }}
              onClick={handleFileManagerClick}
            >
              Pilih dari file manager
            </Button>
          </VStack>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}

export default DropzoneMultiple;
